import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useBillQueue } from '@/hooks/useBillQueue';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  FileText, 
  Edit,
  Search,
  Filter
} from 'lucide-react';

export const ProductionReviewDashboard = () => {
  const { toast } = useToast();
  const { queueItems, isLoading } = useBillQueue();
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'high_priority'>('all');
  const [senderFilter, setSenderFilter] = useState<string>('all');

  // Filter items that need review
  const reviewItems = queueItems.filter(item => 
    item.requires_review || 
    item.manual_review_required ||
    item.processing_status === 'review_required' ||
    (item.validation_errors && item.validation_errors.length > 0)
  );
  const uniqueSenders = Array.from(
    new Set(reviewItems.map(i => i.source_address).filter(Boolean) as string[])
  ).sort();

  // Apply search and filters
  const filteredItems = reviewItems.filter(item => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      if (!item.file_name.toLowerCase().includes(searchLower) &&
          !item.customer_name_raw?.toLowerCase().includes(searchLower) &&
          !item.supplier_name?.toLowerCase().includes(searchLower) &&
          !item.source_address?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }

    // Sender filter
    if (senderFilter !== 'all') {
      if ((item.source_address || '').toLowerCase() !== senderFilter.toLowerCase()) {
        return false;
      }
    }

    // Status filter
    switch (filterStatus) {
      case 'pending':
        return item.processing_status === 'review_required';
      case 'high_priority':
        return (item.ai_confidence || 0) < 0.7 || 
               (item.validation_errors && item.validation_errors.length > 0);
      default:
        return true;
    }
  });

  const handleStartReview = (itemId: string) => {
    navigate(`/dashboard/production-review/${itemId}`);
  };

  const getPriorityBadge = (item: any) => {
    const confidence = item.ai_confidence || 0;
    const hasErrors = item.validation_errors && item.validation_errors.length > 0;
    
    if (confidence < 0.5 || hasErrors) {
      return <Badge variant="destructive">High</Badge>;
    } else if (confidence < 0.7) {
      return <Badge variant="secondary">Medium</Badge>;
    } else {
      return <Badge variant="outline">Low</Badge>;
    }
  };

  const completedToday = queueItems.filter(item => 
    item.processing_status === 'completed' &&
    new Date(item.updated_at).toDateString() === new Date().toDateString()
  ).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-2"></div>
        Loading...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Needs Review</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{reviewItems.length}</div>
            <p className="text-xs text-muted-foreground">Items requiring attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">High Priority</CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {filteredItems.filter(item => 
                (item.ai_confidence || 0) < 0.7 || 
                (item.validation_errors && item.validation_errors.length > 0)
              ).length}
            </div>
            <p className="text-xs text-muted-foreground">Low confidence or errors</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {queueItems.filter(item => item.processing_status === 'processing').length}
            </div>
            <p className="text-xs text-muted-foreground">Currently processing</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed Today</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{completedToday}</div>
            <p className="text-xs text-muted-foreground">Reviews completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by file name, customer, or supplier..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div className="min-w-[220px]">
                <Select value={senderFilter} onValueChange={setSenderFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All senders" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All senders</SelectItem>
                    {uniqueSenders.map((sender) => (
                      <SelectItem key={sender} value={sender.toLowerCase()}>{sender}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                >
                  All
                </Button>
                <Button
                  variant={filterStatus === 'pending' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('pending')}
                >
                  Pending
                </Button>
                <Button
                  variant={filterStatus === 'high_priority' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('high_priority')}
                >
                  High Priority
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Review Queue */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Review Queue
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredItems.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No items require review</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>File</TableHead>
                  <TableHead>Sender</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Confidence</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="font-medium truncate max-w-32">
                        {item.file_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.source_address || 'Unknown'}
                    </TableCell>
                    <TableCell>
                      {item.customer_name_raw || 'Not detected'}
                    </TableCell>
                    <TableCell>
                      {item.supplier_name || 'Not detected'}
                    </TableCell>
                    <TableCell>
                      {item.amount ? `$${item.amount.toFixed(2)}` : 'Not detected'}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={
                          (item.ai_confidence || 0) >= 0.8 ? 'default' :
                          (item.ai_confidence || 0) >= 0.6 ? 'secondary' : 'destructive'
                        }
                      >
                        {((item.ai_confidence || 0) * 100).toFixed(0)}%
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {getPriorityBadge(item)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {item.processing_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStartReview(item.id)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};